/**
 * Mobile version of province navigation menu.
 * Uses click-based accordion interactions optimized for touch devices.
 */

import Link from "next/link";
import { useState } from "react";
import ChevronFill from "@/components/icons/ChevronFill";
import { cn } from "@/lib/utils";
import { generateProvincePageUrl } from "@/lib/province-url-helpers";

const websiteId = process.env.NEXT_PUBLIC_WEBSITE_ID as "nsc" | "jsc" || "nsc";

const MobileProvinceMenuSection = ({ 
  item, 
  provinces 
}: { 
  item: any; 
  provinces: any[];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <li className="leading-0 font-oswald text-base font-bold uppercase">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 uppercase text-white",
          websiteId === "jsc" && "text-black"
        )}
      >
        {item.title}
        <ChevronFill
          className={cn("ml-2 transition-transform", isOpen ? "!rotate-0" : "!rotate-180")}
          color={websiteId === "jsc" ? "black" : "white"}
          direction="down"
        />
      </button>
      
      {isOpen && provinces && provinces.length > 0 && (
        <ul className="flex flex-col gap-6 py-10 pl-10">
          {provinces.map((province: any) => (
            <MobileProvinceMenuItem
              key={province.id}
              province={province}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const MobileProvinceMenuItem = ({ province }: { province: any }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSubPages = province.nsc_provinciepaginas && province.nsc_provinciepaginas.length > 0;

  return (
    <li className="leading-0 font-oswald text-base font-bold uppercase">
      <div className="flex flex-col gap-4">
        {/* Province main link */}
        <div className="flex items-center gap-2">
          <Link
            href={generateProvincePageUrl(province.slug, province.slug)}
            className={cn(
              "text-white transition-colors hover:text-gray-300",
              websiteId === "jsc" && "text-black hover:text-gray-600"
            )}
          >
            {province.title}
          </Link>
          
          {/* Show chevron only if there are sub-pages */}
          {hasSubPages && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={cn(
                "ml-2 text-white",
                websiteId === "jsc" && "text-black"
              )}
            >
              <ChevronFill
                className={cn("transition-transform", isOpen ? "!rotate-0" : "!rotate-180")}
                color={websiteId === "jsc" ? "black" : "white"}
                direction="down"
              />
            </button>
          )}
        </div>
        
        {/* Province sub-pages */}
        {isOpen && hasSubPages && (
          <ul className="flex flex-col gap-4 pl-6">
            {province.nsc_provinciepaginas.map((page: any) => (
              <li key={page.id} className="leading-0 font-oswald text-base font-bold uppercase">
                <Link
                  href={generateProvincePageUrl(province.slug, page.slug)}
                  className={cn(
                    "text-white transition-colors hover:text-gray-300",
                    websiteId === "jsc" && "text-black hover:text-gray-600"
                  )}
                >
                  {page.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

export default MobileProvinceMenuSection; 